import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/** Manual groups the owner creates, plus the automatic lists worked out from orders. */
export function useCustomerGroups() {
  const [groups, setGroups] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/customer-groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return { groups, reloadGroups: load };
}

export function CustomerGroupsBar({ groups, reloadGroups, onPick, active = "" }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (name.trim().length < 2) return toast.error("Give the group a name");
    setBusy(true);
    try {
      await api.post("/admin/customer-groups", { name: name.trim() });
      setName("");
      reloadGroups();
      toast.success("Group created");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (group) => {
    if (!window.confirm(`Delete the group "${group.name}"? Customers stay, only the group goes.`)) return;
    try {
      await api.delete(`/admin/customer-groups/${encodeURIComponent(group.key)}`);
      reloadGroups();
      if (active === group.key) onPick?.("");
      toast.success("Group deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid="customer-groups-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Customer groups · use them to send a WhatsApp update to one set of buyers
        </p>
        <div className="flex items-center gap-2">
          <input
            data-testid="customer-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="New group, e.g. Dealers"
            className="w-44 rounded-full border border-white/10 bg-[#0A0B0E] px-4 py-2 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
          />
          <button
            data-testid="customer-group-create"
            onClick={create}
            disabled={busy}
            className="rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-bold text-cyan-200 disabled:opacity-40"
          >
            Add group
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          data-testid="customer-group-filter-all"
          onClick={() => onPick?.("")}
          className={`rounded-full border px-3.5 py-1.5 text-xs ${active ? "border-white/10 text-slate-400" : "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"}`}
        >
          Everyone
        </button>
        {groups.map((g) => (
          <span
            key={g.key}
            data-testid={`customer-group-chip-${g.key}`}
            className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs ${
              active === g.key
                ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"
                : g.type === "auto"
                  ? "border-fuchsia-400/25 text-fuchsia-200"
                  : "border-white/10 text-slate-300"
            }`}
          >
            <button data-testid={`customer-group-pick-${g.key}`} onClick={() => onPick?.(g.key)}>
              {g.name} <span className="font-mono text-[10px] text-slate-500">{g.count}</span>
            </button>
            {g.type === "manual" && (
              <button
                data-testid={`customer-group-delete-${g.key}`}
                onClick={() => remove(g)}
                title="Delete this group"
                className="text-slate-600 hover:text-red-400"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CustomerGroupPicker({ customer, groups, onSaved }) {
  const [open, setOpen] = useState(false);
  const manual = groups.filter((g) => g.type === "manual");
  const current = customer.groups || [];

  const toggle = async (name) => {
    const next = current.includes(name) ? current.filter((g) => g !== name) : [...current, name];
    try {
      const { data } = await api.put(`/admin/customers/${customer.id}/groups`, { groups: next });
      onSaved?.(customer.id, data.groups);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="relative">
      <button
        data-testid={`customer-groups-btn-${customer.id}`}
        onClick={() => setOpen((v) => !v)}
        className={`max-w-[190px] truncate rounded-lg border px-3 py-1.5 text-left text-xs ${
          current.length ? "border-cyan-400/30 bg-cyan-400/5 text-slate-300" : "border-white/10 text-slate-500 hover:border-cyan-400/40 hover:text-cyan-300"
        }`}
      >
        {current.length ? current.join(", ") : "Add to group"}
      </button>
      {open && (
        <div
          data-testid={`customer-groups-menu-${customer.id}`}
          className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-white/10 bg-[#12141A] p-2 shadow-xl"
        >
          {manual.length === 0 && <p className="px-2 py-1.5 text-xs text-slate-500">Create a group first, above.</p>}
          {manual.map((g) => (
            <button
              key={g.key}
              data-testid={`customer-group-toggle-${customer.id}-${g.key}`}
              onClick={() => toggle(g.name)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-300 hover:bg-white/5"
            >
              <span className={`h-3.5 w-3.5 rounded border ${current.includes(g.name) ? "border-cyan-400 bg-cyan-400" : "border-white/20"}`} />
              {g.name}
            </button>
          ))}
          <button
            data-testid={`customer-groups-close-${customer.id}`}
            onClick={() => setOpen(false)}
            className="mt-1 w-full rounded-lg border border-white/10 px-2 py-1.5 text-[11px] uppercase tracking-[0.15em] text-slate-500 hover:text-slate-300"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
